import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaCheck, FaEye, FaEdit, FaMoneyBillWave, FaWalking } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { syncNicuOutbox } from "../../../services/nicuSyncEngine";
import PageHeading from "../../PageHeading";
import NicuViewBillModal from "./NicuViewBillModal";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import ExportOptions from "../../ExportOptions";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import TableSkeleton from "../../TableSkeleton";
import { useAuth } from "../../../utils/AuthContext";
import NicuBillModal from "./NicuBillModal";
import DynamicSelect from "../../DynamicSelect";
import { BsThreeDotsVertical } from "react-icons/bs";
import SmartDropdown from "../../Shared/SmartDropdown";
import NicuAddPaymentModal from "./NicuAddPaymentModal";
import { NicuPrintLayout } from "../../NicuPrintLayout";
import { FaPrint } from "react-icons/fa";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const NicuBill = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [viewBillModalOpen, setViewBillModalOpen] = useState(false);
  const [viewBillData, setViewBillData] = useState(null);
  const [editBillId, setEditBillId] = useState(null);
  const [editBillData, setEditBillData] = useState(null);
  const [bills, setBills] = useState([]);
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentBillData, setPaymentBillData] = useState(null);
  const [printBill, setPrintBill] = useState(null);

  // --- FILTER STATES ---
  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTriggerRefs = useRef({});
  const [releaseModalOpen, setReleaseModalOpen] = useState(false);
  const [billToRelease, setBillToRelease] = useState(null);

  const actionButtonClass =
    "size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300";
  const { user } = useAuth();

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  // --- FETCH DATA ---
  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    const url = `https://api.muktihospital.com/api/nicu/nicu-bills`;

    const params = {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchQuery,
      type: filterType
    };

    if (filterType === 'daily') {
       params.startDate = startDate;
       params.endDate = endDate;
    } else if (filterType === 'monthly') {
       const [y, m] = selectedMonth.split('-');
       params.year = y;
       params.month = m;
    } else if (filterType === 'yearly') {
       params.year = selectedYear;
    }

    const cacheKey = `nicu_bills_${JSON.stringify(params)}`;

    try {
      const response = await axios.get(url, {
        headers: {
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
        params: params,
      });

      const { bills, total } = response.data;
      setBills(bills || []);
      setTotalRecords(total || 0);
      setTotalPages(Math.ceil((total || 0) / itemsPerPage) || 1);

      // Cache this filter so it can be shown offline later.
      setNicuOfflineActive(false);
      saveToCache(cacheKey, {
        bills: bills || [],
        total: total || 0,
      });
    } catch (err) {
      console.error("Error fetching bills:", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(cacheKey);
        if (cached && cached.bills) {
          setBills(cached.bills);
          setTotalRecords(cached.total || 0);
          setTotalPages(Math.ceil((cached.total || 0) / itemsPerPage) || 1);
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved bills.");
        } else {
          setBills([]);
          setTotalRecords(0);
          setTotalPages(1);
          setNicuOfflineActive(false);
          toast.info("Offline: this filter isn't saved yet. Open it online once.");
        }
      } else {
        toast.error("Failed to load bills.");
        setBills([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchQuery, itemsPerPage, filterType, startDate, endDate, selectedMonth, selectedYear]);

  // --- EFFECTS ---
  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  useEffect(() => {
    const onSynced = (e) => {
      const count = e.detail?.synced || 0;
      if (count > 0) {
        toast.success(`${count} offline record${count > 1 ? "s" : ""} synced.`, { autoClose: 2500 });
      }
      fetchBills();
    };
    window.addEventListener("nicu-synced", onSynced);
    if (navigator.onLine) {
      syncNicuOutbox();
    }
    return () => window.removeEventListener("nicu-synced", onSynced);
  }, [fetchBills]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, filterType, startDate, endDate, selectedMonth, selectedYear]);

  const handleRefreshBills = useCallback(async () => {
      await fetchBills();
  }, [fetchBills]);

const handleReleaseClick = (bill) => {
    // Check Payment Status logic remains the same
    const netAmount = parseFloat(bill.netAmount || 0);
    const paidAmount = parseFloat(bill.paidAmount || 0);
    
    if (paidAmount < netAmount - 0.01) {
        const due = (netAmount - paidAmount).toFixed(2);
        toast.warn(`Cannot release patient. Bill is not fully paid. Due: ৳${due}`);
        return;
    }

    // Instead of window.confirm, open the modal
    setBillToRelease(bill);
    setReleaseModalOpen(true);
  };

  const confirmRelease = async () => {
    if (!billToRelease) return;

    try {
        await axios.put(`https://api.muktihospital.com/api/nicu/nicu-bills/release/${billToRelease.id}`, {}, {
            headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" }
        });

        toast.success("Patient released successfully!");
        handleRefreshBills(); 
    } catch (error) {
        console.error("Release error:", error);
        toast.error(error.response?.data?.error || "Failed to release patient.");
    } finally {
        // Close modal and reset state
        setReleaseModalOpen(false);
        setBillToRelease(null);
    }
  };

  const handleAddMoney = (bill) => {
    setPaymentBillData(bill);
    setPaymentModalOpen(true);
  };

  // ---- Direct bill print (same copy as the bill modal) ----
  const handlePrintBill = async (bill) => {
    try {
      const res = await axios.get(
        `https://api.muktihospital.com/api/nicu/nicu-bills/${bill.id}`,
        { headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" } }
      );
      const full = res.data.bill || res.data;

      // pending pathology slips for the same patient
      let pathologySlips = [];
      try {
        const pRes = await axios.get(
          `https://api.muktihospital.com/api/pathology-bill/nicu-slip/list?regNo=${encodeURIComponent(full.regNo || "")}`,
          { headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" } }
        );
        pathologySlips = (pRes.data.bills || []).filter((b) => (b.dueAmount || 0) > 0);
      } catch (e) {
        console.error("Pathology due lookup failed:", e);
      }

      setPrintBill({ ...full, pathologySlips });
    } catch (err) {
      console.error("Print failed:", err);
      toast.error("Failed to load the bill for printing.");
    }
  };

  // print once the layout has rendered, then unmount it
  useEffect(() => {
    if (!printBill) return;
    const t = setTimeout(() => {
      window.print();
      setTimeout(() => setPrintBill(null), 500);
    }, 350);
    return () => clearTimeout(t);
  }, [printBill]);

  // --- CLIENT SIDE SEARCH FILTER ---
  const filteredBills = useMemo(() => {
      if (!debouncedSearchQuery) return bills;
      const lowerQuery = debouncedSearchQuery.toLowerCase();
      return bills.filter(bill => 
          (bill.billNumber && bill.billNumber.toLowerCase().includes(lowerQuery)) ||
          (bill.patientName && bill.patientName.toLowerCase().includes(lowerQuery))
      );
  }, [bills, debouncedSearchQuery]);

  const currentData = useMemo(() => {
      const start = (currentPage - 1) * itemsPerPage;
      return filteredBills.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, filteredBills]);

  // Recalculate pagination when search changes
  useEffect(() => {
      if(debouncedSearchQuery){
          setTotalPages(Math.ceil(filteredBills.length / itemsPerPage) || 1);
          setTotalRecords(filteredBills.length);
      } else {
          setTotalPages(Math.ceil(bills.length / itemsPerPage) || 1);
          setTotalRecords(bills.length);
      }
      setCurrentPage(1);
  }, [debouncedSearchQuery, filteredBills.length, bills.length, itemsPerPage]);


  // --- HANDLERS ---
  const handleDelete = useCallback(async (billIds) => {
    try {
      await Promise.all(
        billIds.map((id) =>
          axios.delete(`https://api.muktihospital.com/api/nicu/nicu-bills/delete/${id}`, {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          })
        )
      );
      await handleRefreshBills();
      setSelectedRows([]);
      toast.success(`${billIds.length} bill(s) deleted successfully!`, {
        autoClose: 2000,
      });
    } catch (err) {
      console.error("Error deleting bills:", {
        message: err.message,
        response: err.response?.data,
      });
      toast.error(err.response?.data?.error || "Failed to delete bill(s).", {
        autoClose: 2000,
      });
    }
  }, [handleRefreshBills]);

  const handleBillUpdate = useCallback((updatedBill) => {
    setBills((prevBills) =>
      prevBills.map((bill) =>
        bill.id === updatedBill.id ? { ...bill, ...updatedBill } : bill
      )
    );
    setViewBillData((prev) =>
      prev && prev.id === updatedBill.id ? { ...prev, ...updatedBill } : prev
    );
  }, []);

  const handleEdit = useCallback(async (bill) => {
      try {
        if (!bill?.id) {
          toast.error("Invalid bill ID", { autoClose: 2000 });
          return;
        }
        
        const response = await axios.get(`https://api.muktihospital.com/api/nicu/nicu-bills/${bill.id}`, {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });

        const fullBillData = response.data.bill || response.data;

        setEditBillId(fullBillData.id);
        setEditBillData(fullBillData);
        setGenerateBillModalOpen(true);

      } catch (err) {
        console.error("Error fetching bill for edit:", {
          message: err.message,
          response: err.response?.data,
        });
        toast.error(err.response?.data?.error || "Failed to load bill for editing.", {
          autoClose: 2000,
        });
      }
    }, []);

  const handleView = useCallback((bill) => {
    setViewBillData(bill);
    setViewBillModalOpen(true);
  }, []);

  const handlePageChange = useCallback((page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedRows([]);
    }
  }, [totalPages]);

  const handleSelectAll = useCallback((e) => {
    const validIds = currentData.filter((bill) => bill.id != null).map((bill) => bill.id);
    setSelectedRows(e.target.checked ? validIds : []);
  }, [currentData]);

  const handleRowSelect = useCallback((billId) => {
    if (billId == null) return;
    setSelectedRows((prev) =>
      prev.includes(billId) ? prev.filter((id) => id !== billId) : [...prev, billId]
    );
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "SL No",
    "BILL NO",
    "Patient Name",
    "DATE",
    "DISCOUNT (৳)",
    "NET AMOUNT (৳)",
    "PAID AMOUNT (৳)",
    "SUBMITTED BY",
    "AUTHORIZED BY",
    "STATUS",
  ];

  const skeletonColumns = [
    { width: 20, height: 20, isCircle: true, className: "print-hidden-in-print" },
    { width: 50, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 120, height: 16, className: "print-hidden-in-print" },
  ];

  const dataMapper = (bill) => [
    (currentPage - 1) * itemsPerPage + bills.indexOf(bill) + 1,
    bill.billNumber || "-",
    bill.patientName || "",
    bill.createdAt
      ? new Date(bill.createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "-",
    bill.discount?.toFixed(2) || "0.00",
    bill.netAmount?.toFixed(2) || "0.00",
    bill.paidAmount?.toFixed(2) || "0.00",
    user?.name || "Unknown",
  ].join("\t");

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "NICU" },
      { label: "NICU Bills" },
    ],
    []
  );

  const allVisibleSelected = useMemo(
    () => currentData.length > 0 && currentData.every((bill) => bill.id && selectedRows.includes(bill.id)),
    [currentData, selectedRows]
  );
  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Nicu Bills" breadcrumbs={breadcrumbs} />
      </div>
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}
      <div className="bg-white shadow-sm rounded-lg">
        {/* --- Header Controls --- */}
        <div className="flex justify-between flex-wrap border-b border-dashed border-M-text-color/50 p-4 gap-4 print-hidden">
          <div className="flex items-center justify-between gap-3">
              <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Bill List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}
                aria-label="Delete selected bills"
                title="Delete selected bills"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <input
               type="text"
               className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
               placeholder="Search by Bill No, Name, Phone or ID..."
               value={searchQuery}
               onChange={handleSearchChange}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start justify-start sm:justify-end gap-3 w-full sm:w-auto">
            
            {/* --- FILTER UI --- */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="w-32">
                  <DynamicSelect 
                    options={filterOptions} 
                    value={filterOptions.find(o => o.value === filterType)} 
                    onChange={o => setFilterType(o.value)} 
                    isSearchable={false}
                    placeholder="Filter"
                  />
                </div>

                {filterType === "daily" && (
                  <>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">From:</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-32" />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">To:</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-32" />
                      </div>
                  </>
                )}
                {filterType === "monthly" && (
                  <div className="flex items-center gap-2">
                      <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm outline-none" />
                  </div>
                )}
                {filterType === "yearly" && (
                    <div className="flex items-center gap-2">
                      <input type="number" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-24" min="2000" max="2100" />
                    </div>
                )}
            </div>       
            <div className="flex items-center justify-between gap-3 sm:w-auto w-full">
              <button
                onClick={() => {
                  setEditBillId(null);
                  setEditBillData(null);
                  setGenerateBillModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 px-4 py-2 text-sm"
              >
                NICU Bill
              </button>
              <ExportOptions
                data={bills}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Nicu Bill List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <style>
          {`
            @media print {
              .print-hidden, .print-hidden-in-print {
                display: none !important;
              }
              .print-all-rows {
                display: table-row !important;
              }
              .print-table {
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th, .print-table td {
                border: 1px solid #000;
                padding: 8px;
                text-align: left;
                font-size: 12px;
              }
              .print-table th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              .print-table tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            }
          `}
        </style>
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table
              className="min-w-[1440px] w-full divide-y divide-gray-200 print-table"
              role="table"
            >
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print" scope="col">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        disabled={bills.length === 0}
                        aria-label="Select all bills on this page"
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="selectAll"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                      scope="col"
                    >
                      {header}
                    </th>
                  ))}
                  <th
                    className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print"
                    scope="col"
                  >
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} />
                ) : bills.length === 0 ? (
                  <tr className="print-hidden"><td colSpan={11} className="text-center py-5 text-gray-500">No bills found.</td></tr>
                ) : (
                  <>
                    {bills.map((bill, index) => {
                      const isFullyPaid = (bill.paidAmount || 0) >= (bill.netAmount || 0);
                      const isReleased = bill.admissionStatus === "Released" || bill.status === "Released";

                      return (
                        <tr key={`normal-${bill.id}`} className="border-b hover:bg-gray-50 print-hidden">
                           <td
                          className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print"
                          role="cell"
                        >
                          <div className="relative left-3 -top-[2px]">
                            <input
                              type="checkbox"
                              id={`bill-${index}`}
                              className="hidden peer"
                              checked={selectedRows.includes(bill.id)}
                              onChange={() => handleRowSelect(bill.id)}
                              aria-label={`Select bill ${bill.billNumber || "unknown"}`}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={`bill-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                            ></label>
                          </div>
                        </td>
                          <td className="p-3 text-sm">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td className="p-3 text-sm">{bill.billNumber || "-"}</td>
                          <td className="p-3 text-sm">{bill.patientName || ""}</td>
                          <td className="p-3 text-sm">{bill.createdAt ? new Date(bill.createdAt).toLocaleDateString() : "-"}</td>
                          <td className="p-3 text-sm">{bill.discount?.toFixed(2) || "0.00"}</td>
                          <td className="p-3 text-sm">{bill.netAmount?.toFixed(2) || "0.00"}</td>
                          <td className="p-3 text-sm">{bill.paidAmount?.toFixed(2) || "0.00"}</td>
                          <td className="p-3 text-sm">{user?.name || "Unknown"}</td>
                          <td className="p-3 text-sm">{bill.authorizedBy || "N/A"}</td>
                          <td className="p-3 text-sm">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isFullyPaid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                              {isFullyPaid ? "Complete" : "Pending"}
                            </span>
                          </td>
                          
                          {/* ACTIONS COLUMN */}
                          <td className="p-3 text-right print-hidden-in-print">
                            <button
                              ref={(el) => (dropdownTriggerRefs.current[bill.id] = el)}
                              onClick={() => setActiveDropdown(activeDropdown === bill.id ? null : bill.id)}
                              className="size-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500"
                            >
                              <BsThreeDotsVertical size={14} />
                            </button>

                            <SmartDropdown
                              isOpen={activeDropdown === bill.id}
                              triggerRef={{ current: dropdownTriggerRefs.current[bill.id] }}
                              onClose={() => setActiveDropdown(null)}
                              options={{ width: 160, align: 'right' }}
                            >
                              <div className="py-1">
                                {/* Add Money */}
                                <button onClick={() => { handleAddMoney(bill); setActiveDropdown(null); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                  <FaMoneyBillWave className="text-green-600" /> Add Money
                                </button>

                                {/* View */}
                                <button onClick={() => { handleView(bill); setActiveDropdown(null); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                  <FaEye className="text-blue-500" /> View Details
                                </button>

                                {/* Print the bill copy */}
                                <button onClick={() => { handlePrintBill(bill); setActiveDropdown(null); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                  <FaPrint className="text-gray-600" /> Print Bill
                                </button>

                                {/* Edit */}
                                <button onClick={() => { handleEdit(bill); setActiveDropdown(null); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                  <FaEdit className="text-green-600" /> Edit Bill
                                </button>

                                {/* RELEASE PATIENT */}
                                {!isReleased && (
                                      <button 
                                          onClick={() => { handleReleaseClick(bill); setActiveDropdown(null); }}
                                          disabled={!isFullyPaid}
                                          className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                              isFullyPaid 
                                              ? "text-gray-700 hover:bg-green-50 hover:text-green-700 cursor-pointer" 
                                              : "text-gray-400 cursor-not-allowed"
                                          }`}
                                          title={!isFullyPaid ? "Bill must be fully paid to release" : "Release Patient"}
                                      >
                                        <FaWalking className={isFullyPaid ? "text-green-600" : "text-gray-300"} /> Release
                                      </button>
                                  )}

                                {/* Delete */}
                                <button onClick={() => { setSelectedRows([bill.id]); setDeleteModalOpen(true); setActiveDropdown(null); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-2">
                                  <RiDeleteBin6Line className="text-red-500" /> Delete
                                </button>
                              </div>
                            </SmartDropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                  {totalRecords}
                </p>
                <nav aria-label="Pagination">
                  <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === 1 ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                        aria-disabled={currentPage === 1}
                      >
                        <MdKeyboardArrowLeft />
                      </button>
                    </li>
                    {totalPages <= 10 ? (
                      [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1 ? "bg-M-primary-color text-white" : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                            aria-label={`Go to page ${i + 1}`}
                            aria-current={currentPage === i + 1 ? "page" : undefined}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    ) : (
                      (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;
                        for (let i = 1; i <= Math.min(firstPages, totalPages); i++) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i ? "bg-M-primary-color text-white" : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }
                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li key="ellipsis" className="table-pagination-item">
                              ...
                            </li>
                          );
                        }
                        for (
                          let i = Math.max(firstPages + 1, totalPages - lastPages + 1);
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i ? "bg-M-primary-color text-white" : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }
                        return pages;
                      })()
                    )}
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === totalPages ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                        aria-disabled={currentPage === totalPages}
                      >
                        <MdKeyboardArrowRight />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={releaseModalOpen} 
        onClose={() => { setReleaseModalOpen(false); setBillToRelease(null); }} 
        onConfirm={confirmRelease}
        ids={billToRelease ? [billToRelease.id] : []}
        entityName="patient release"
        title="Confirm Patient Release"
        confirmText="Release"
        cancelText="Cancel"
        message={`Are you sure you want to release patient ${billToRelease?.patientName}? This will update the admission status to Released.`}
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedRows([]);
        }}
        onConfirm={() => handleDelete(selectedRows)}
        ids={selectedRows}
        entityName="bill"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} bill(s)?`
            : `Do you really want to delete this bill?`
        }
      />
      <NicuBillModal
        isOpen={generateBillModalOpen}
        onClose={() => {
          setGenerateBillModalOpen(false);
          setEditBillId(null);
          setEditBillData(null);
        }}
        onSave={async (newBill) => { await handleRefreshBills(); }}
        billId={editBillId}
        initialData={editBillData}
      />
      <NicuViewBillModal
        isOpen={viewBillModalOpen}
        onClose={() => {
          setViewBillModalOpen(false);
          setViewBillData(null);
        }}
        bill={viewBillData}
        onEdit={handleBillUpdate}
        onDelete={handleDelete}
      />
      {printBill && (
        <NicuPrintLayout
          className="nicu-bill-print"
          title="NICU Bill"
          compact
          isPaid={(printBill.payableAmount || 0) <= 0}
          billInfo={
            <div style={{ border: "1px solid #000", padding: "5px 8px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", columnGap: "14px", rowGap: "1px" }}>
                <p style={{ margin: 0 }}><strong>Bill No:</strong> {printBill.billNumber || "N/A"}</p>
                <p style={{ margin: 0 }}><strong>Reg No:</strong> {printBill.regNo || "N/A"}</p>
                <p style={{ margin: 0 }}><strong>Special No:</strong> {printBill.specialNo || "N/A"}</p>

                <p style={{ margin: 0 }}><strong>Patient:</strong> {printBill.patientName || "N/A"}</p>
                <p style={{ margin: 0 }}><strong>Age / Sex:</strong> {printBill.age || "--"} / {printBill.gender || "--"}</p>
                <p style={{ margin: 0 }}><strong>Bed No:</strong> {printBill.bedNo || "--"}</p>

                <p style={{ margin: 0 }}><strong>Admit Date:</strong> {printBill.admissionDate ? new Date(printBill.admissionDate).toLocaleDateString("en-GB") : "N/A"}</p>
                <p style={{ margin: 0 }}><strong>Bill Date:</strong> {printBill.billingDate ? new Date(printBill.billingDate).toLocaleDateString("en-GB") : (printBill.createdAt ? new Date(printBill.createdAt).toLocaleDateString("en-GB") : "N/A")}</p>

                <p style={{ margin: 0, gridColumn: "1 / -1" }}><strong>Consultant:</strong> {printBill.consultantName || "N/A"}</p>
              </div>
            </div>
          }
          table={
            <>
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>Sl</th>
                  <th>Service Name</th>
                  <th style={{ width: "16%" }}>Unit Price</th>
                  <th style={{ width: "12%" }}>Qty</th>
                  <th style={{ width: "18%" }}>Amount (৳)</th>
                </tr>
              </thead>
              <tbody>
                {(printBill.serviceItems || []).map((item, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{item.serviceName || "N/A"}{item.particulars ? ` — ${item.particulars}` : ""}</td>
                    <td>{parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                    <td>{item.totalUnit || "0"}</td>
                    <td>{parseFloat(item.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </>
          }
          summary={
            <>
              <div><span>Bill Amount</span><span>৳ {Math.round(printBill.billAmount || 0).toFixed(2)}</span></div>
              <div><span>Service Charge</span><span>৳ {Math.round(printBill.serviceCharge || 0).toFixed(2)}</span></div>
              <div><span>Discount</span><span>৳ {Math.round(printBill.discount || 0).toFixed(2)}</span></div>
              <div><span>Net Amount</span><span>৳ {Math.round(printBill.netAmount || 0).toFixed(2)}</span></div>
              <div><span>Paid Amount</span><span>৳ {Math.round(printBill.paidAmount || 0).toFixed(2)}</span></div>
              <div><span>Payable (Due)</span><span>৳ {Math.round(printBill.payableAmount || 0).toFixed(2)}</span></div>
            </>
          }
          note={
            printBill.pathologySlips?.length > 0 ? (
              <div>
                <p style={{ margin: 0, fontWeight: "bold" }}>
                  Pending Pathology Dues: ৳ {Math.round(printBill.pathologySlips.reduce((s, b) => s + (b.dueAmount || 0), 0)).toFixed(2)}
                </p>
                <p style={{ margin: "4px 0 0" }}>
                  {printBill.pathologySlips.map((b) => `${b.billNumber} (৳${Math.round(b.dueAmount)})`).join(", ")}
                </p>
              </div>
            ) : null
          }
          footer={
            <>
              <div className="np-sign-item">
                <div className="np-sign-space" /><div className="np-sign-line" />
                <p className="np-sign-label">Prepared By</p>
              </div>
              <div className="np-sign-item">
                <div className="np-sign-space" /><div className="np-sign-line" />
                <p className="np-sign-label">Authorized By</p>
              </div>
              <div className="np-sign-item">
                <div className="np-sign-space" /><div className="np-sign-line" />
                <p className="np-sign-label">Accounts Officer</p>
              </div>
            </>
          }
        />
      )}

      <NicuAddPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
            setPaymentModalOpen(false);
            setPaymentBillData(null);
        }}
        bill={paymentBillData}
        onPaymentSuccess={handleRefreshBills} 
      />
    </div>
  );
};

export default NicuBill;